import React, { useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import {
  Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";

/**
 * Mobile-friendly searchable combobox built on shadcn Command + Popover.
 * - Type to filter (case-insensitive, best matches float to the top via cmdk scoring)
 * - Scroll the full list
 * - Optional free-text entry (allowCustom) and clear button (clearable)
 * options: array of strings
 */
export const Combobox = ({
  value,
  onChange,
  options = [],
  placeholder = "Select",
  searchPlaceholder = "Search\u2026",
  emptyText = "No results",
  allowCustom = true,
  clearable = false,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const choose = (v) => {
    onChange(v);
    setQuery("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(""); }}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="h-11 w-full justify-between px-3 font-normal"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || placeholder}
          </span>
          <span className="ml-2 flex shrink-0 items-center gap-1">
            {clearable && value && (
              <span
                role="button"
                aria-label="Clear"
                onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); }}
                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronsUpDown className="h-4 w-4 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="z-[70] p-0"
        style={{ width: "var(--radix-popover-trigger-width)" }}
      >
        <Command>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={searchPlaceholder}
            className="h-11"
          />
          <CommandList className="max-h-[45vh]">
            <CommandEmpty>
              {allowCustom && query.trim() ? (
                <button
                  type="button"
                  onClick={() => choose(query.trim())}
                  className="mx-auto flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-brand hover:bg-accent"
                >
                  <Check className="h-4 w-4" /> Use &ldquo;{query.trim()}&rdquo;
                </button>
              ) : (
                <span className="text-sm text-muted-foreground">{emptyText}</span>
              )}
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  onSelect={() => choose(opt)}
                  className="py-2.5 text-[15px]"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt ? "opacity-100 text-brand" : "opacity-0")} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
